import { getQueryString, reorderBundle } from '@medplum/core'
import type {
  Bundle,
  BundleEntry,
  Coding,
  Reference,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import { getDisplayString } from '#src/display.ts'
import { base64Encode } from '#src/encoding.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import {
  FhirPathPatchEncodeError,
  jsonPatchToFhirPathPatch,
} from '#src/fhirpath-patch/index.ts'
import { invariant } from '#src/invariant.ts'
import { merge } from '#src/merge/merge.ts'
import {
  type DeepPushOnlyArray,
  monitorResource,
  type PatchOperation,
} from '#src/monitorResource.ts'
import { normalizeQuery, type QueryTypes } from '#src/search.ts'
import { addTag } from '#src/tags.ts'
import { generateUrnUuid } from '#src/utils.ts'

// TODO: possibly create a `DeepPartial` type?
type PartialBundleEntry<T extends Resource = Resource> = Omit<
  BundleEntry<T>,
  'request'
> & {
  request: Partial<BundleEntry['request']>
}

// Wire format for PATCH entries.
// - 'json-patch' (default): Binary `application/json-patch+json` body —
//   today's production format.
// - 'fhirpath-patch': FHIR `Parameters` fhirpath-patch body carrying semantic
//   intent (`where(...)`-keyed paths). Converted back to JSON patch at
//   submit time by the client (`convertFhirPathEntriesToJsonPatch`) until
//   Medplum accepts fhirpath patch at the transport layer.
export type BundlePatchFormat = 'json-patch' | 'fhirpath-patch'

type BundleMutationBuilderOptions = {
  generateUrnUuid?: () => string
  tag?: Coding
  patchFormat?: BundlePatchFormat
}

interface PatchCallOptions {
  // Overrides the builder-level patchFormat for this call.
  patchFormat?: BundlePatchFormat
  // Caller-owned test operations are preserved on the wire and make a version
  // conflict non-rebasable. Use them when a write is valid only while an
  // invariant observed immediately before patch construction still holds.
  preconditions?: PatchOperation<ResourceType>[]
}

// Metadata for a PATCH entry, used for:
// - Cache invalidation in the React hook layer (resourceType + id)
// - Observability (definitionStack captured at patch() call-time so it
//   points to the React component, not the infrastructure code at submit)
export interface PatchEntryContext {
  resourceType: ResourceType
  id: string
  definitionStack: string | undefined
}

export class BundleMutationBuilder {
  private request: Bundle
  private generateUrnUuid: NonNullable<
    BundleMutationBuilderOptions['generateUrnUuid']
  >
  private tag?: Coding
  private patchFormat: BundlePatchFormat
  private patchContexts: Map<string, PatchEntryContext> = new Map()

  constructor(
    type: 'batch' | 'transaction',
    options?: BundleMutationBuilderOptions
  ) {
    this.request = {
      resourceType: 'Bundle',
      type,
      entry: [],
    }
    this.generateUrnUuid = options?.generateUrnUuid ?? generateUrnUuid
    this.tag = options?.tag
    this.patchFormat = options?.patchFormat ?? 'json-patch'
  }

  private addEntry(entry: BundleEntry): void {
    this.request.entry?.push(entry)
  }

  private maybeAddTag<
    TResource extends Resource,
    TResult extends TResource | ExtendedResource<TResource>,
  >(resource: TResult): TResult {
    if (this.tag) {
      if (resource instanceof ExtendedResource) {
        resource.addTag(this.tag)
      } else {
        addTag(resource, this.tag)
      }
    }
    return resource
  }

  create<TResource extends Resource>(
    resource: TResource | ExtendedResource<TResource>,
    overrides?: PartialBundleEntry<TResource>
  ): Reference<TResource> {
    let fhirResource: TResource
    if (resource instanceof ExtendedResource) {
      fhirResource = this.maybeAddTag(resource).toFhirResource()
    } else {
      fhirResource = this.maybeAddTag(resource)
    }

    const [entry] = merge<PartialBundleEntry<TResource>>({
      current: {
        resource: fhirResource,
      },
      incoming: overrides,
    })
    invariant(entry?.resource?.resourceType, 'Resource type is required')

    const fullUrl = entry.fullUrl ?? this.generateUrnUuid()

    this.addEntry({
      fullUrl,
      ...entry,
      request: {
        ...entry?.request,
        method: 'POST',
        url: entry.resource.resourceType,
      },
    })

    return { reference: fullUrl, display: getDisplayString(entry.resource) }
  }

  update<TResource extends Resource>(
    resource: TResource,
    overrides?: PartialBundleEntry<TResource>
  ): Reference<TResource> {
    const [entry] = merge<PartialBundleEntry<TResource>>({
      current: {
        resource: this.maybeAddTag(resource),
      },
      incoming: overrides,
    })
    invariant(
      entry?.resource?.resourceType &&
        // resource id is not required for update request if the url is provided
        // such as with an upsert request where the identifier is derived from the search/query string
        (entry.request?.url ?? entry?.resource?.id),
      'Resource type and ID are required for update request'
    )

    const fullUrl = entry.fullUrl ?? this.generateUrnUuid()

    this.addEntry({
      fullUrl,
      ...entry,
      request: {
        method: 'PUT',
        url: `${entry.resource.resourceType}/${entry.resource.id}`,
        ...entry?.request,
      },
    })

    return { reference: fullUrl, display: getDisplayString(entry.resource) }
  }

  upsert<TResource extends Resource>(
    resource: TResource | ExtendedResource<TResource>,
    query: QueryTypes,
    overrides?: PartialBundleEntry<TResource>
  ): Reference<TResource> {
    let fhirResource: TResource
    if (resource instanceof ExtendedResource) {
      fhirResource = this.maybeAddTag(resource).toFhirResource()
    } else {
      fhirResource = this.maybeAddTag(resource)
    }

    return this.update(fhirResource, {
      ...overrides,
      request: {
        ...overrides?.request,
        method: 'PUT',
        url: `${fhirResource.resourceType}?${getQueryString(normalizeQuery(query))}`,
      },
    })
  }

  delete<TResource extends Resource>(
    resource: TResource | ExtendedResource<TResource>,
    overrides?: PartialBundleEntry<TResource>
  ): void {
    const [entry] = merge<PartialBundleEntry<TResource>>({
      current: {
        resource:
          resource instanceof ExtendedResource
            ? resource.toFhirResource()
            : resource,
      },
      incoming: overrides,
    })
    invariant(
      entry?.resource?.resourceType && entry?.resource?.id,
      'Resource type and ID are required for delete request'
    )

    const fullUrl = entry.fullUrl ?? this.generateUrnUuid()

    this.addEntry({
      fullUrl,
      ...entry,
      request: {
        ...entry?.request,
        method: 'DELETE',
        url: `${entry.resource.resourceType}/${entry.resource.id}`,
      },
    })
  }

  patch<TResource extends Resource>(
    resource: TResource,
    patchFn: (resource: DeepPushOnlyArray<TResource>) => void,
    overrideFullUrl?: string,
    opts?: PatchCallOptions
  ): void
  patch<
    TResource extends Resource,
    TExtendedResource extends ExtendedResource<TResource>,
  >(
    resource: TExtendedResource,
    patchFn: (resource: TExtendedResource) => void,
    overrideFullUrl?: string,
    opts?: PatchCallOptions
  ): void
  patch<TResource extends Resource>(
    resource: TResource | ExtendedResource<TResource>,
    patchFn: (
      resource: DeepPushOnlyArray<TResource> | ExtendedResource<TResource>
    ) => void,
    overrideFullUrl?: string,
    opts?: PatchCallOptions
  ): void {
    let patches: PatchOperation<TResource['resourceType']>[]
    let type: ResourceType
    let baseResource: Resource

    if (resource instanceof ExtendedResource) {
      patches = resource.mutate((proxy) => {
        this.maybeAddTag(proxy)
        patchFn(proxy)
      })
      type = resource.fhirType
      // mutate() restores the pre-mutation resource, so this snapshot
      // reflects server state, not the patched state.
      baseResource = resource.toFhirResource()
    } else {
      invariant(
        resource.resourceType && resource.id,
        'Resource type and ID are required for patch request'
      )
      const monitoredResource = monitorResource(resource)
      const proxy = monitoredResource.proxy as DeepPushOnlyArray<TResource>
      this.maybeAddTag(proxy as TResource)
      patchFn(proxy)
      patches = monitoredResource.patches
      type = resource.resourceType
      // monitorResource proxies a deep clone, so `resource` is unmutated.
      baseResource = resource
    }

    const versionId = baseResource.meta?.versionId
    const fullUrl = overrideFullUrl ?? this.generateUrnUuid()

    if (patches.length === 0) {
      return
    }

    const entryResource = this.buildPatchEntryResource(
      patches,
      baseResource,
      versionId,
      opts?.patchFormat ?? this.patchFormat,
      opts?.preconditions ?? []
    )

    this.addEntry({
      resource: entryResource,
      request: {
        method: 'PATCH',
        url: `${type}/${resource.id}`,
      },
      fullUrl,
    })

    // Captured here so observability surfaces the React caller of `bundle.patch`,
    // not the infrastructure code that runs at submission time.
    this.patchContexts.set(fullUrl, {
      resourceType: type,
      id: resource.id ?? '',
      definitionStack: new Error('bundle.patch caller').stack,
    })
  }

  private buildPatchEntryResource(
    patches: PatchOperation<ResourceType>[],
    baseResource: Resource,
    versionId: string | undefined,
    patchFormat: BundlePatchFormat,
    preconditions: PatchOperation<ResourceType>[]
  ): Resource {
    invariant(
      preconditions.every((operation) => operation.op === 'test'),
      'Patch preconditions must be test operations'
    )
    // fhirpath-patch needs the base history ref, which embeds the versionId —
    // require it up front. Without a versionId we cannot build the precondition,
    // so fall through to json-patch (which omits the test when versionId is
    // absent) rather than throwing from the ref builder.
    if (
      patchFormat === 'fhirpath-patch' &&
      versionId &&
      preconditions.length === 0
    ) {
      try {
        // The version test is NOT encoded as a fhirpath op — it travels in the
        // base history-ref sidecar and is re-prepended as a JSON-pointer `test`
        // op at submit-time conversion, keeping retry/arePatchOperationsSafe
        // intact.
        return jsonPatchToFhirPathPatch(patches, baseResource, { baseResource })
      } catch (error) {
        if (!(error instanceof FhirPathPatchEncodeError)) {
          throw error
        }
        // Not every JSON-patch value is representable as fhirpath-patch
        // (e.g. anonymous BackboneElements) — fall back to the legacy wire
        // format for this entry rather than failing the mutation.
        console.warn(
          `[bundle-builder] falling back to json-patch for ${baseResource.resourceType}/${baseResource.id}: ${error.message}`
        )
      }
    }

    // Prepend a JSON Patch `test` op on meta.versionId when available. This
    // turns stale-cache writes into atomic failures at the server instead of
    // silently clobbering concurrent changes. The retry layer in
    // executeBatch uses arePatchOperationsSafe to gate op-level retry.
    const finalPatches: PatchOperation<ResourceType>[] = versionId
      ? [
          { op: 'test' as const, path: '/meta/versionId', value: versionId },
          ...preconditions,
          ...patches,
        ]
      : [...preconditions, ...patches]

    return {
      resourceType: 'Binary',
      contentType: 'application/json-patch+json',
      data: base64Encode(JSON.stringify(finalPatches)),
    }
  }

  getPatchContexts(): ReadonlyMap<string, PatchEntryContext> {
    return this.patchContexts
  }

  get bundle(): Bundle {
    // `reorderBundle` _only_ works w/ use of `BundleEntry.fullUrl` which is only really relevant in the context of mutations
    // as such, the builder is scoped to mutations only
    // https://github.com/medplum/medplum/issues/5177
    // https://www.medplum.com/docs/fhir-datastore/fhir-batch-requests#internal-references
    return reorderBundle(this.request)
  }
}

export class TransactionMutationBuilder extends BundleMutationBuilder {
  constructor(options?: BundleMutationBuilderOptions) {
    super('transaction', options)
  }
}

export class BatchMutationBuilder extends BundleMutationBuilder {
  constructor(options?: BundleMutationBuilderOptions) {
    super('batch', options)
  }
}
